/**
 * What a row's Type and Action actually mean, in words.
 *
 * The trail records `epic_reordered` and `access_get_task`, which are precise
 * and say nothing to a reader who did not write them. These are the hover
 * explanations for the audit page and the task History section.
 *
 * ⚠️ FAMILIES, NOT A LIST OF EVERY ACTION. There are 65+ actions plus one per
 * MCP tool, and an explicit dictionary would be a second list to keep in step -
 * one that says NOTHING at all the day an action is added and nobody updates it.
 * Matching on the entity prefix means a new `risk_escalated` still explains
 * itself, and `describeAction` falls back to the naming convention rather than
 * to silence.
 */

/** `access_<route>` and `access_<tool>` — a recorded READ, never a change. */
const ACCESS_PREFIX = 'access_';

/** The entity half of `entity_verb`, longest first so `wiki_template` beats `wiki`. */
const FAMILIES = [
    ['wiki_template_page', 'a page inside a wiki template'],
    ['wiki_project_page', 'a page in a client\'s wiki'],
    ['wiki_template', 'a wiki template'],
    ['wiki_image', 'an image in the wiki'],
    ['script_version', 'a version of a script'],
    ['primary_contact', 'the client\'s primary contact'],
    ['project_kickoff', 'the project\'s kick-off date'],
    ['project_weekend_work', 'whether the project plans weekend work'],
    // ⚠️ BEFORE the bare `project` family below, which would otherwise claim it
    // and explain this as "the project record" - true but useless. The point of
    // the row is which FIGURE moved.
    ['project_effort', 'the effort estimate typed on the whole project'],
    ['project_rollback', 'the whole project, rolled back'],
    ['task_order', 'the order tasks sit in'],
    ['task_parent', 'which main task a subtask belongs to'],
    ['dependency', 'a dependency between two tasks'],
    ['maintenance', 'portal maintenance mode'],
    ['password', 'an account password'],
    ['mfa', 'an account\'s second factor'],
    ['subtask', 'a subtask'],
    ['maintask', 'a main task'],
    ['template', 'a project template'],
    ['comment', 'a comment'],
    ['script', 'a script in the repository'],
    ['project', 'the project record'],
    ['session', 'a sign-in session'],
    ['epic', 'an epic (a Kanban column)'],
    ['risk', 'a risk on the register'],
    ['issue', 'an issue'],
    ['lead', 'a lead in the sales pipeline'],
    ['task', 'a task'],
    ['user', 'a portal user'],
    ['login', 'a sign-in'],
    ['admin_login', 'an admin sign-in'],
    ['client_login', 'a client sign-in'],
    ['mcp', 'the connector credentials'],
    // The email trail (T439/T444). Three families, longest first by the sort:
    // cognito_email and email_delivery must both beat bare email.
    ['cognito_email', "one of Cognito's own emails (an invite, a reset code)"],
    ['email_delivery', 'the delivery of an email'],
    ['email', 'an email from the portal']
];

/** The verb half. Anything unlisted is described by the action name itself. */
const VERBS = [
    ['created', 'was created'],
    ['updated', 'was changed'],
    ['deleted', 'was deleted'],
    ['restored', 'was restored'],
    ['cloned', 'was copied'],
    ['added', 'was added'],
    ['edited', 'was changed'],
    ['removed', 'was removed'],
    ['reordered', 'was moved in the order'],
    ['linked', 'was linked'],
    ['unlinked', 'was unlinked'],
    ['invited', 'was invited'],
    ['invite_resent', 'had their invitation sent again'],
    ['role_changed', 'had their role changed'],
    ['disabled', 'was disabled'],
    ['enabled', 'was enabled'],
    ['signed_off', 'was signed off'],
    ['signoff_removed', 'had its sign-off removed'],
    ['status_changed', 'had its status changed'],
    ['approved', 'was approved'],
    ['rejected', 'was rejected'],
    ['published', 'was published'],
    ['draft_saved', 'had a draft saved'],
    ['success', 'succeeded'],
    ['failed', 'FAILED'],
    ['changed', 'was changed'],
    ['won', 'was marked won'],
    ['transferred', 'was transferred'],
    ['revealed', 'were revealed'],
    ['enrolled', 'was enrolled'],
    ['upload_url_issued', 'had an upload permission issued'],
    // The record-first send pair, and the outcomes SES reports back (T439).
    ['send_intent', 'was about to be sent — this row was written BEFORE the send, and without it nothing goes'],
    ['send_outcome', 'finished its send attempt — expand for the MessageId, or the refusal'],
    ['confirmed', 'was confirmed'],
    ['sent', 'was sent']
];

const longestFirst = pairs => [...pairs].sort((a, b) => b[0].length - a[0].length);

/**
 * A sentence for one action.
 *
 * @param {string} action e.g. `task_updated`, `access_get_task`
 * @returns {string}
 */
export function describeAction(action) {
    if (typeof action !== 'string' || !action) return 'No action was recorded on this row.';

    if (action.startsWith(ACCESS_PREFIX)) {
        const what = action.slice(ACCESS_PREFIX.length).replace(/_/g, ' ');
        return `Someone READ ${what}. Nothing was changed — access rows record what was `
            + 'seen and when, which is what a disputed sign-off turns on.';
    }

    const family = longestFirst(FAMILIES).find(([prefix]) => action.startsWith(`${prefix}_`));
    const verb = longestFirst(VERBS).find(([suffix]) => action.endsWith(`_${suffix}`));

    if (family && verb) {
        const subject = family[1][0].toUpperCase() + family[1].slice(1);
        return `${subject} ${verb[1]}. Expand the row to see exactly what.`;
    }

    // The convention, rather than silence: every action is entity_verb, so a
    // reader can still take it apart even when it is new to this list.
    const [entity, ...rest] = action.split('_');
    return `Recorded as \`${action}\` — actions are named entity_verb, so this concerns `
        + `${entity} (${rest.join(' ') || 'no verb recorded'}). Expand the row to see what changed.`;
}

/** The Type badge: change, access or denied. */
export function describeType({ access, denied }) {
    if (denied) {
        return 'Someone tried to read this and was REFUSED — a wrong or missing token, or a '
            + 'request that could not be honoured. Recorded so that a turned-away attempt is '
            + 'not the one thing the trail misses.';
    }
    if (access) {
        return 'A READ, not a change: someone looked at this client\'s data. Client portal '
            + 'reads are recorded too, because what a client saw and when is what a disputed '
            + 'sign-off argument turns on.';
    }
    return 'A CHANGE: something was created, edited or deleted. Expand the row for the '
        + 'field-by-field before and after.';
}

/** The "no change" badge. */
export const NO_CHANGE_EXPLANATION =
    'This row recorded a value that did not actually change — the client portal used to log '
    + 'every field it sent, changed or not. It is kept rather than hidden, because nothing is '
    + 'filtered out of an audit trail, but it is not counted in Net.';

/** The Origin column. */
export function describeOrigin(via) {
    switch (via) {
        case 'admin portal':
            return 'Written by a person clicking in the admin portal.';
        case 'client portal':
            return 'Written by a person clicking in the client portal.';
        case 'admin connector':
            return 'Written by an AI agent through the ADMIN MCP connector, acting on an '
                + 'admin\'s behalf — not by someone clicking.';
        case 'client connector':
            return 'Written by an AI agent through the CLIENT MCP connector, acting on a '
                + 'client\'s behalf — not by someone clicking.';
        default:
            return 'No origin recorded. Rows written before 2026-08-16 carry no source, and '
                + 'nothing here guesses one.';
    }
}
